import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useQuery } from '@apollo/client/react'
import { createWebClient } from '@/services/ApolloClient'
import { useUser } from '@/services/UserService'
import { GetDefaultWatchListQuery } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { GET_DEFAULT_WATCHLIST } from './queries'

interface WatchlistContextValue {
  client: ReturnType<typeof createWebClient>
  viewerProfileId?: string
  /** Check if a title is in the user's watchlist (O(1) lookup from cached data) */
  isInWatchlist: (titleId: string) => boolean
  /** Refetch the watchlist after add/remove operations */
  refetchWatchlist: () => Promise<void>
  /** Whether the watchlist data is currently loading */
  isWatchlistLoading: boolean
}

const WatchlistContext = createContext<WatchlistContextValue | undefined>(undefined)

export const WatchlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale } = useLocale()
  const { userId, isLoggedIn } = useUser()
  // TODO: when web implements user profiles, use that profile id here instead.
  const viewerProfileId = userId ?? undefined

  const client = useMemo(() => {
    return createWebClient({ locale, viewerProfileId })
  }, [locale, viewerProfileId])

  // Fetch full watchlist once for logged-in users
  const {
    data,
    refetch,
    loading,
    error: watchlistError,
  } = useQuery<GetDefaultWatchListQuery>(GET_DEFAULT_WATCHLIST, {
    client,
    skip: !isLoggedIn || !viewerProfileId,
    fetchPolicy: 'cache-and-network',
    context: {
      requireAuth: true,
    },
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (watchlistError) {
      const isAuthError =
        (CombinedGraphQLErrors.is(watchlistError) &&
          watchlistError.errors.some((e) => e.extensions?.code === 'UNAUTHENTICATED')) ||
        watchlistError.message?.toLowerCase().includes('unauthorized') ||
        watchlistError.message?.toLowerCase().includes('unauthenticated') ||
        watchlistError.message?.toLowerCase().includes('no viewer profile id')

      if (isAuthError) {
        logger().error('Watchlist query attempted without authentication', { viewerProfileId }, watchlistError)
      } else {
        logger().error('Failed to fetch watchlist', { viewerProfileId }, watchlistError)
      }
    }
  }, [watchlistError, viewerProfileId])

  // Create a Set of content IDs for O(1) lookup
  const watchlistIds = useMemo(() => {
    const ids = new Set<string>()
    data?.getMyDefaultWatchList?.items.edges?.forEach((edge) => {
      const contentId = edge?.node?.content?.id
      if (contentId) ids.add(contentId)
    })
    return ids
  }, [data])

  const isInWatchlist = useCallback(
    (titleId: string): boolean => {
      if (!isLoggedIn || !viewerProfileId) return false
      return watchlistIds.has(titleId)
    },
    [watchlistIds, isLoggedIn, viewerProfileId],
  )

  const refetchWatchlist = useCallback(async (): Promise<void> => {
    if (isLoggedIn && viewerProfileId) {
      await refetch()
    }
  }, [refetch, isLoggedIn, viewerProfileId])

  const value = useMemo(
    () => ({
      client,
      viewerProfileId,
      isInWatchlist,
      refetchWatchlist,
      isWatchlistLoading: loading,
    }),
    [client, viewerProfileId, isInWatchlist, refetchWatchlist, loading],
  )

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>
}

export const useWatchlistContext = () => {
  const context = useContext(WatchlistContext)
  if (!context) {
    throw new Error('useWatchlistContext must be used within a WatchlistProvider')
  }
  return context
}
